import React, { useState } from 'react';
import { Button, Input, Popconfirm, Radio, Select, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import type {
  ExpV2AddOverridesResult,
  ExpV2Override,
  ExpV2SubjectType,
  ExpV2Test,
} from '@/types/experimentsV2';
import { CopyableId } from '@/components/CopyableId';
import { OVERRIDE_MAX, parseIds } from './actions';

interface Props {
  test: ExpV2Test;
  overrides: ExpV2Override[];
  canEdit: boolean;
  /** Resolves with the server's tally, or nothing when the add failed (the page says why). */
  onAdd: (
    subjectType: ExpV2SubjectType,
    ids: string[],
    variant: string
  ) => void | Promise<ExpV2AddOverridesResult | void>;
  onRemove: (id: string) => void | Promise<void>;
}

/** QA: force named players (or devices) into a variant, ahead of the hash. */
export const OverridesPanel: React.FC<Props> = ({ test, overrides, canEdit, onAdd, onRemove }) => {
  const [text, setText] = useState('');
  const [subjectType, setSubjectType] = useState<ExpV2SubjectType>('user');
  const [variant, setVariant] = useState<string | undefined>();
  const [result, setResult] = useState<ExpV2AddOverridesResult | null>(null);
  const [adding, setAdding] = useState(false);

  const parsed = parseIds(text, OVERRIDE_MAX, { lowercase: subjectType === 'user' });
  const variants = test.variants.filter((v) => v.state !== 'removed').map((v) => v.key);

  const add = async () => {
    if (parsed.problem || !variant) return;
    setAdding(true);
    try {
      const r = await onAdd(subjectType, parsed.ids, variant);
      if (r) {
        setResult(r);
        setText('');
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} data-section="overrides">
      {canEdit && (
        <>
          <Radio.Group
            value={subjectType}
            onChange={(e) => setSubjectType(e.target.value as ExpV2SubjectType)}
          >
            <Radio value="user">Player ids</Radio>
            <Radio value="device">Device ids</Radio>
          </Radio.Group>
          <Input.TextArea
            id="expv2-override-ids"
            rows={3}
            placeholder={`Up to ${OVERRIDE_MAX} ids, one per line or separated by commas`}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Space wrap>
            <Select
              id="expv2-override-variant"
              style={{ width: 200 }}
              placeholder="Variant"
              value={variant}
              options={variants.map((k) => ({ value: k, label: k }))}
              onChange={(v: string) => setVariant(v)}
            />
            <Button
              type="primary"
              loading={adding}
              disabled={Boolean(parsed.problem) || !variant}
              onClick={() => void add()}
            >
              Force into variant
            </Button>
            {text.trim() !== '' && parsed.problem && (
              <Typography.Text type="danger" data-problem>
                {parsed.problem}
              </Typography.Text>
            )}
          </Space>
          {result && (
            <Typography.Text data-override-result>
              Added {result.added}, changed {result.changed}, unchanged {result.unchanged}, periods
              closed {result.periodsClosed}
            </Typography.Text>
          )}
        </>
      )}
      <Typography.Text type="secondary">
        A device override applies only to the player bound to that device.
      </Typography.Text>
      <Table<ExpV2Override>
        size="small"
        rowKey="id"
        dataSource={overrides}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        onRow={(r) => ({ 'data-override': r.id }) as React.HTMLAttributes<HTMLElement>}
        columns={[
          { title: 'Type', dataIndex: 'subjectType' },
          {
            title: 'Id',
            dataIndex: 'subjectId',
            render: (v: string) => <CopyableId value={v} />,
          },
          { title: 'Variant', dataIndex: 'variant' },
          {
            title: 'Added',
            dataIndex: 'createdAt',
            render: (v: string) => dayjs(v).format('D MMM HH:mm'),
          },
          ...(canEdit
            ? [
                {
                  title: '',
                  key: 'remove',
                  render: (_: unknown, r: ExpV2Override) => (
                    <Popconfirm
                      title="Remove this override? The player goes back to the hash."
                      onConfirm={() => onRemove(r.id)}
                    >
                      <Button size="small" danger>
                        Remove
                      </Button>
                    </Popconfirm>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Space>
  );
};
